/**
 * A project's budget from the firm's own estimate (issue #132 part A): the hours used against the
 * budget per work type, a percent bar, the warning at the company's share, the progress tap and the
 * forecast; the administrator's drawer that reads an estimate file into lines and saves them only on
 * «Lagre budsjett»; and Hugin's suggestions over the budgets (budgetAdvice.ts) for Prosjekter and Oversikt.
 */
import { errorText } from '@/api/client';
import {
  type BudgetLine,
  readEstimate,
  useBudgets,
  useProjectBudget,
  useProjectReport,
  useProjectWord,
  useProjectsOn,
  useSaveBudget,
  useSetProgress,
} from '@/api/projects';
import { useShiftTypes } from '@/api/shifts';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Drawer, Empty, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HuginNotes } from '../vaktplan/Hugin';
import type { Apply } from '../vaktplan/advice';
import { budgetNudges, forecastLine, h1, kr } from './budgetAdvice';

const PROGRESS = [25, 50, 75, 100];

/** A bar of `used` against `budget`: amber from the warning share, red past the budget. */
export function Bar({ percent, warnAt, label }: { percent: number | null; warnAt: number; label: string }) {
  if (percent == null) return null;
  const tone = percent > 100 ? 'over' : percent >= warnAt ? 'warn' : 'ok';
  return (
    <span
      className={`pjbar ${tone}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.min(percent, 100)}
      aria-label={label}
    >
      <span style={{ width: `${Math.min(percent, 100)}%` }} />
    </span>
  );
}

/** The budget card for one project, under the projects table when one is picked. */
export function BudgetCard({ id, admin }: { id: number; admin: boolean }) {
  const word = useProjectWord();
  const q = useProjectBudget(id);
  const setProgress = useSetProgress();
  const [editing, setEditing] = useState(false);
  const b = q.data;
  if (q.isError) return null;
  const lines = b?.lines ?? [];
  return (
    <section className="mcard" id="pj-budsjett" data-testid="pj-budget">
      <div className="mch">
        <div>
          <h2>{b ? T('Budsjett: {name}', { name: b.name }) : T('Budsjett')}</h2>
          <div className="sub">
            {T('Timer brukt mot budsjettet fra overslaget, godkjente og ventende, siden starten.')}
          </div>
        </div>
        {admin && (
          <>
            <span className="sp" />
            <Button small onClick={() => setEditing(true)}>
              {lines.length ? T('Endre budsjett') : T('Legg inn budsjett')}
            </Button>
          </>
        )}
      </div>
      {!b ? (
        <Empty>{T('Laster …')}</Empty>
      ) : !lines.length ? (
        <Empty>
          {admin
            ? T(
                'Ingen budsjett ennå. Last opp overslaget som PDF, Excel, CSV eller bilde, så leser Hugin det inn. Eller skriv linjene selv.',
              )
            : T('Ingen budsjett ennå.')}
        </Empty>
      ) : (
        <div className="pjbudget">
          <div className="pjsum">
            <b className="tnum" data-testid="pj-budget-used">
              {T('{used} av {budget} t brukt', { used: h1(b.used), budget: h1(b.budget) })}
              {b.percent != null ? `, ${b.percent} %` : ''}
            </b>
            <Bar
              percent={b.percent}
              warnAt={b.warn_percent}
              label={T('{p} prosent av budsjetterte timer brukt', { p: b.percent ?? 0 })}
            />
            <small>
              {b.left >= 0
                ? T('{left} t igjen', { left: h1(b.left) })
                : T('{over} t over budsjettet', { over: h1(-b.left) })}
              {b.waiting ? `, ${T('{w} t venter på godkjenning', { w: h1(b.waiting) })}` : ''}
              {b.materials != null ? `. ${T('Materialer i budsjettet: {kr}', { kr: kr(b.materials) })}` : ''}
            </small>
            {b.warn && (
              <small className="pjwarn" data-testid="pj-budget-warn">
                {T('Over varslingsgrensen på {p} % av timene.', { p: b.warn_percent })}
              </small>
            )}
          </div>
          <table className="mt cards">
            <thead>
              <tr>
                <th>{T('Arbeidstype')}</th>
                <th className="num">{T('Budsjett')}</th>
                <th className="num">{T('Brukt')}</th>
                <th aria-label={T('Andel')} />
              </tr>
            </thead>
            <tbody>
              {b.by_type.map((t) => {
                const p = t.budget > 0 ? Math.round((t.used / t.budget) * 100) : null;
                const name = t.work_type ?? T('Uten arbeidstype');
                return (
                  <tr key={t.shift_type_id ?? 'none'}>
                    <td>{name}</td>
                    <td className="num tnum" data-k={T('Budsjett')}>
                      {t.budget ? `${h1(t.budget)} t` : T('Ikke budsjettert')}
                    </td>
                    <td className="num tnum" data-k={T('Brukt')}>
                      {h1(t.used)} t
                    </td>
                    <td>
                      <Bar percent={p} warnAt={b.warn_percent} label={`${name}: ${p ?? 0} %`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="pjprog">
            <span>{T('Hvor langt er jobben kommet?')}</span>
            {admin ? (
              <span className="pjtaps">
                {PROGRESS.map((p) => (
                  <Button
                    key={p}
                    small
                    {...(b.progress === p ? { tone: 'pri' as const } : {})}
                    aria-pressed={b.progress === p}
                    disabled={setProgress.isPending}
                    onClick={() =>
                      setProgress.mutate(
                        { id, progress: b.progress === p ? null : p },
                        { onError: (e) => toast(errorText(e)) },
                      )
                    }
                  >
                    {p} %
                  </Button>
                ))}
              </span>
            ) : (
              <b>{b.progress != null ? `${b.progress} %` : T('Ikke satt')}</b>
            )}
          </div>
          {forecastLine(b) && (
            <p className={`pjfore${(b.overrun ?? 0) > 0 ? ' over' : ''}`} data-testid="pj-forecast">
              {forecastLine(b)}
            </p>
          )}
          {b.progress == null && (
            <p className="hint">
              {T(
                'Sett hvor langt jobben er kommet, så regner Timefly ut timene ved ferdig fra forbruket så langt.',
              )}
            </p>
          )}
        </div>
      )}
      {admin && editing && b && (
        <BudgetDrawer
          id={id}
          name={b.name}
          lines={lines}
          word={word.lower}
          onClose={() => setEditing(false)}
        />
      )}
    </section>
  );
}

type Draft = BudgetLine & { key: number };
let seq = 0;
const draftOf = (l: BudgetLine): Draft => ({
  key: ++seq,
  label: l.label,
  shift_type_id: l.shift_type_id,
  hours: l.hours,
  materials: l.materials,
});
const numIn = (v: string) => {
  const n = Number(v.replace(/\s/g, '').replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
};

/**
 * The administrator's budget: an estimate file read into lines (nothing saved), the lines checked
 * and changed by hand, and saved as a whole on «Lagre budsjett».
 */
export function BudgetDrawer({
  id,
  name,
  lines,
  word,
  onClose,
}: {
  id: number;
  name: string;
  lines: BudgetLine[];
  word: string;
  onClose: () => void;
}) {
  const { tenant } = useTenant();
  const types = useShiftTypes().data ?? [];
  const save = useSaveBudget();
  const [draft, setDraft] = useState<Draft[]>(() => lines.map(draftOf));
  const [read, setRead] = useState<{ source: string; note: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const change = (key: number, patch: Partial<BudgetLine>) =>
    setDraft((d) => d.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const r = await readEstimate(id, file);
      setDraft(r.lines.map(draftOf));
      setRead({ source: r.source, note: r.note });
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const valid = draft.every((l) => l.label.trim());
  const submit = () =>
    save.mutate(
      {
        id,
        lines: draft.map(({ key: _k, ...l }) => ({
          label: l.label.trim(),
          shift_type_id: l.shift_type_id,
          hours: l.hours,
          materials: l.materials,
        })),
      },
      {
        onSuccess: () => {
          toast(T('Budsjettet er lagret'));
          onClose();
        },
        onError: (e) => toast(errorText(e)),
      },
    );
  const total = draft.reduce((a, l) => a + (l.hours ?? 0), 0);
  const materials = draft.reduce((a, l) => a + (l.materials ?? 0), 0);
  return (
    <Drawer
      open
      onClose={onClose}
      title={T('Budsjett for {name}', { name })}
      footer={
        <>
          <span className="sp" />
          <Button onClick={onClose}>{T('Avbryt')}</Button>
          <Button tone="pri" onClick={submit} disabled={save.isPending || busy || !valid}>
            {T('Lagre budsjett')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 14 }} data-testid="pj-budget-drawer">
        <label className="fld">
          <span>{T('Last opp overslaget')}</span>
          <input
            type="file"
            data-testid="pj-estimate-file"
            accept=".pdf,.xlsx,.csv,image/*"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
        <p className="hint">
          {T(
            'PDF, Excel, CSV eller et bilde av tilbudet. CSV og Excel leses direkte; en PDF eller et bilde leser Hugin. Ingenting lagres før du trykker Lagre budsjett.',
          )}
        </p>
        {tenant?.is_demo && (
          <p className="hint" data-testid="pj-sample">
            {T('Prøv med eksempelet på Nye vinduer blokk B:')}{' '}
            <a href="/demo/tilbud-vinduer-blokk-b.pdf" download>
              PDF
            </a>{' '}
            ,{' '}
            <a href="/demo/tilbud-vinduer-blokk-b.csv" download>
              CSV
            </a>
          </p>
        )}
        {busy && <output className="hint">{T('Leser overslaget …')}</output>}
        {read && (
          <output className="al info" data-testid="pj-estimate-read">
            {read.source === 'model'
              ? T('Hugin leste {n} linjer. Sjekk dem før du lagrer.', { n: draft.length })
              : T('Leste {n} linjer fra filen. Sjekk dem før du lagrer.', { n: draft.length })}
            {read.note ? ` ${read.note}` : ''}
          </output>
        )}
        <table className="mt pjlines">
          <thead>
            <tr>
              <th>{T('Post')}</th>
              <th>{T('Arbeidstype')}</th>
              <th className="num">{T('Timer')}</th>
              <th className="num">{T('Materialer (kr)')}</th>
              <th aria-label={T('Fjern')} />
            </tr>
          </thead>
          <tbody>
            {draft.map((l, i) => (
              <tr key={l.key} data-testid="pj-line">
                <td>
                  <input
                    className="inp"
                    aria-label={T('Post {n}', { n: i + 1 })}
                    value={l.label}
                    onChange={(e) => change(l.key, { label: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    className="inp"
                    aria-label={T('Arbeidstype, post {n}', { n: i + 1 })}
                    value={l.shift_type_id ?? ''}
                    onChange={(e) =>
                      change(l.key, { shift_type_id: e.target.value ? Number(e.target.value) : null })
                    }
                  >
                    <option value="">{T('Ingen')}</option>
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="num">
                  <input
                    className="inp num"
                    inputMode="decimal"
                    aria-label={T('Timer, post {n}', { n: i + 1 })}
                    defaultValue={l.hours != null ? String(l.hours).replace('.', ',') : ''}
                    onChange={(e) => change(l.key, { hours: numIn(e.target.value) })}
                  />
                </td>
                <td className="num">
                  <input
                    className="inp num"
                    inputMode="decimal"
                    aria-label={T('Materialer i kroner, post {n}', { n: i + 1 })}
                    defaultValue={l.materials != null ? String(l.materials).replace('.', ',') : ''}
                    onChange={(e) => change(l.key, { materials: numIn(e.target.value) })}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="mlink"
                    aria-label={T('Fjern post {n}', { n: i + 1 })}
                    onClick={() => setDraft((d) => d.filter((x) => x.key !== l.key))}
                  >
                    <Icon name="close" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mbar">
          <Button
            small
            onClick={() =>
              setDraft((d) => [
                ...d,
                draftOf({ label: '', shift_type_id: null, hours: null, materials: null }),
              ])
            }
          >
            {T('Legg til linje')}
          </Button>
          <span className="sp" />
          <b className="tnum" data-testid="pj-draft-total">
            {T('{h} t', { h: h1(total) })}
            {materials ? `, ${kr(materials)}` : ''}
          </b>
        </div>
        <p className="hint">
          {T('Timene sammenlignes med registrerte timer på {what} per arbeidstype (vakttypen).', {
            what: word,
          })}
        </p>
      </div>
    </Drawer>
  );
}

/**
 * Hugin's unasked suggestions over the budgets and the month (budgetAdvice.ts), for Prosjekter and
 * Oversikt: nothing without the projects module and the hours.
 */
export function ProjectNotes({ folded = false }: { folded?: boolean }) {
  const on = useProjectsOn();
  const may = useCan()('timeliste.se');
  const word = useProjectWord();
  const nav = useNavigate();
  const d = today();
  const ym = d.slice(0, 7);
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const last = `${ym}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
  const budgets = useBudgets().data?.projects;
  const month = useProjectReport(`${ym}-01`, last, false).data?.projects;
  if (!on || !may || !budgets) return null;
  const list = budgetNudges({ budgets, month, today: d, word });
  const go = (a: Apply) => {
    if (a.kind === 'open') nav(a.to);
  };
  return <HuginNotes list={list} onChoice={go} onOpen={() => {}} folded={folded} />;
}
